import { useCallback, useEffect, useRef } from 'react'
import { useAgentRuntime } from '../context/AgentProvider'

export type RuntimeEventHandler<E extends Event = Event> = (event: E) => void

/**
 * Subscribe to `runtime.events` with a stable listener and automatic cleanup.
 *
 * This avoids re-subscribing on every render while still calling the latest handler.
 */
export const useRuntimeEventListener = <E extends Event = Event>(
  type: string,
  handler: RuntimeEventHandler<E>,
  deps: unknown[] = [],
  options?: boolean | AddEventListenerOptions
) => {
  const runtime = useAgentRuntime()
  const handlerRef = useRef(handler)

  const memoHandler = useCallback(handler, deps)
  handlerRef.current = memoHandler

  const stableListener = useCallback((event: Event) => {
    handlerRef.current(event as E)
  }, [])

  // removeEventListener only cares about capture boolean (not the full options object)
  const capture = typeof options === 'boolean' ? options : options?.capture

  useEffect(() => {
    runtime.events.addEventListener(type, stableListener as EventListener, options)
    return () => {
      runtime.events.removeEventListener(type, stableListener as EventListener, capture)
    }
    // Intentionally do not depend on `options` object identity; use capture instead.
  }, [runtime, type, stableListener, capture])
}

